import { create } from "zustand";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { teamsApi } from "../api/client";
import { captureException } from "../observability/sentry";

const ACTIVE_TEAM_KEY = (userId: string) => `@cynosure/active_team_id:${userId}`;

export interface Team {
  id: string;
  team_type: string;
  name_encrypted: string | null;
  encryption_metadata: Record<string, unknown>;
  owner_user_id: string;
  current_key_version: number;
  created_at: string;
  updated_at: string | null;
}

interface TeamState {
  teams: Team[];
  activeTeamId: string | null;
  isLoading: boolean;
}

interface TeamActions {
  fetchTeams: () => Promise<void>;
  setActiveTeam: (teamId: string | null) => void;
  clearTeams: () => void;
  initializeForUser: (userId: string) => Promise<void>;
}

export const useTeamStore = create<TeamState & TeamActions>((set, get) => ({
  teams: [],
  activeTeamId: null,
  isLoading: false,

  fetchTeams: async () => {
    set({ isLoading: true });
    try {
      const response = await teamsApi.getMyTeams();
      const teams: Team[] = response.data;
      const { activeTeamId } = get();
      // Default to personal team if no active team is set
      if (activeTeamId === null && teams.length > 0) {
        const personalTeam = teams.find((t) => t.team_type === "personal");
        set({ teams, isLoading: false, activeTeamId: personalTeam?.id ?? teams[0].id });
      } else {
        set({ teams, isLoading: false });
      }
    } catch (error) {
      console.error("Failed to fetch teams:", error);
      captureException(error, { context: "teamStore.fetchTeams" });
      set({ isLoading: false });
    }
  },

  setActiveTeam: (teamId: string | null) => {
    set({ activeTeamId: teamId });
    // Persist to AsyncStorage (best-effort, need userId from elsewhere)
    AsyncStorage.getItem("@cynosure/user")
      .then((userJson) => {
        if (userJson) {
          try {
            const user = JSON.parse(userJson);
            if (user.id) {
              if (teamId === null) {
                AsyncStorage.removeItem(ACTIVE_TEAM_KEY(user.id)).catch(() => {});
              } else {
                AsyncStorage.setItem(ACTIVE_TEAM_KEY(user.id), teamId).catch(() => {});
              }
            }
          } catch {
            // ignore
          }
        }
      })
      .catch(() => {});
  },

  clearTeams: () => {
    set({ teams: [], activeTeamId: null, isLoading: false });
  },

  initializeForUser: async (userId: string) => {
    try {
      const savedTeamId = await AsyncStorage.getItem(ACTIVE_TEAM_KEY(userId));
      if (savedTeamId) {
        set({ activeTeamId: savedTeamId });
      }
    } catch (error) {
      captureException(error, { context: "teamStore.initializeForUser" });
    }
  },
}));
